///
/// EarthBrowser
///
/// Created by Matt Giger on 9/24/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Floating properties window for an image ground overlay: preview,
/// opacity slider, and the lat/lon corner bounds that place the picture
/// on the globe. Edits dispatch straight into the window's imageOverlay
/// state; the engine's GroundOverlayActivity applies them as uniform
/// updates on the next frame.
///

import { SaveToCloudButton } from "@/components/features/user-datasets/SaveToCloudButton"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import { DatasetBrowserWindow } from "@/components/windows/DatasetBrowserWindow"
import { useRegisterWindowContent } from "@/components/windows/WindowContentRegistry"
import type { DatasetBrowserWindowState, GeoBounds } from "@/redux/dialogSlice"
import { updateDatasetWindow } from "@/redux/dialogSlice"
import { useAppDispatch } from "@/redux/store"
import { Image as ImageIcon } from "lucide-react"
import { memo, useCallback, useEffect, useMemo, useState } from "react"

interface ImageOverlayBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

type Edge = keyof GeoBounds

const cEdgeLimits: Record<Edge, { min: number; max: number }> = {
	west: { min: -180, max: 180 },
	east: { min: -180, max: 180 },
	south: { min: -85, max: 85 },
	north: { min: -85, max: 85 },
}

export const ImageOverlayBrowserRenderer = memo(function ImageOverlayBrowserRenderer({
	windowId,
	windowState,
}: ImageOverlayBrowserRendererProps) {
	const dispatch = useAppDispatch()
	const overlay = windowState.imageOverlay

	// Edited-but-uncommitted edge values (commit on blur/Enter so typing
	// "-1..." doesn't fight the clamp)
	const [drafts, setDrafts] = useState<Partial<Record<Edge, string>>>({})

	// External bounds changes (recapture, story mount) reset drafts
	useEffect(() => {
		setDrafts({})
	}, [overlay?.bounds])

	const commitEdge = useCallback((edge: Edge, raw: string) => {
		setDrafts(prev => ({ ...prev, [edge]: undefined }))
		if (!overlay) return
		const value = parseFloat(raw)
		if (!isFinite(value)) return
		const limits = cEdgeLimits[edge]
		const clamped = Math.max(limits.min, Math.min(limits.max, value))
		const bounds = { ...overlay.bounds, [edge]: clamped }
		if (bounds.west >= bounds.east || bounds.south >= bounds.north) return
		dispatch(updateDatasetWindow({
			id: windowId,
			updates: { imageOverlay: { ...overlay, bounds } },
		}))
	}, [dispatch, overlay, windowId])

	const setOpacity = useCallback((values: number[]) => {
		if (!overlay || values.length === 0) return
		const opacity = Math.max(0, Math.min(1, values[0] / 100))
		dispatch(updateDatasetWindow({
			id: windowId,
			updates: { imageOverlay: { ...overlay, opacity } },
		}))
	}, [dispatch, overlay, windowId])

	const edgeInput = useCallback((edge: Edge, label: string) => {
		if (!overlay) return null
		const value = drafts[edge] ?? String(overlay.bounds[edge])
		return (
			<div className="flex flex-col items-center gap-1">
				<label htmlFor={`${windowId}-${edge}`} className="text-[10px] uppercase tracking-wider text-slate-400">{label}</label>
				<Input
					id={`${windowId}-${edge}`}
					inputMode="decimal"
					aria-label={`Overlay ${edge} edge (degrees)`}
					value={value}
					onChange={e => setDrafts(prev => ({ ...prev, [edge]: e.target.value }))}
					onBlur={e => commitEdge(edge, e.target.value)}
					onKeyDown={e => {
						if (e.key === "Enter") commitEdge(edge, (e.target as HTMLInputElement).value)
					}}
					className="h-8 w-24 text-center text-xs rounded-xl border-2 border-slate-700 bg-slate-900/80 text-slate-100 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
				/>
			</div>
		)
	}, [commitEdge, drafts, overlay, windowId])

	const content = useMemo(() => {
		if (!overlay) return null
		const opacityPct = Math.round(overlay.opacity * 100)
		return (
			<div className="flex flex-col gap-4 p-4 text-slate-200 overflow-y-auto h-full">
				{windowState.plotImageData && (
					<img
						src={windowState.plotImageData}
						alt={`${windowState.title} preview`}
						className="w-full max-h-36 object-contain rounded-xl border-2 border-slate-700 bg-slate-900/60"
					/>
				)}
				<div className="text-[11px] text-slate-400 text-center">
					{overlay.naturalWidth} × {overlay.naturalHeight} px
				</div>

				<div className="flex flex-col gap-2">
					<div className="flex items-center justify-between">
						<span className="text-xs font-medium text-slate-300">Opacity</span>
						<span className="text-xs tabular-nums text-slate-400">{opacityPct}%</span>
					</div>
					<Slider
						value={[opacityPct]}
						min={0}
						max={100}
						step={1}
						onValueChange={setOpacity}
						aria-label="Overlay opacity"
					/>
				</div>

				<div className="flex flex-col gap-2">
					<span className="text-xs font-medium text-slate-300">Bounds (degrees)</span>
					<div className="flex justify-center">{edgeInput("north", "North")}</div>
					<div className="flex justify-between gap-2">
						{edgeInput("west", "West")}
						{edgeInput("east", "East")}
					</div>
					<div className="flex justify-center">{edgeInput("south", "South")}</div>
				</div>

				<div className="flex items-center justify-between mt-auto pt-2 border-t border-slate-700">
					<span className="text-xs text-slate-400">
						{windowState.cloudDataset ? "Saved to cloud" : "Save to use in stories & shares"}
					</span>
					<SaveToCloudButton windowId={windowId} title={windowState.title} />
				</div>
			</div>
		)
	}, [edgeInput, overlay, setOpacity, windowId, windowState.cloudDataset, windowState.plotImageData, windowState.title])

	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<ImageIcon className="w-5 h-5" />}
			iconColor="text-teal-400"
		>
			{content}
		</DatasetBrowserWindow>
	)
})
